import { useState } from "react";
import { Link } from "react-router-dom";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import CookieSettings from "./CookieSettings";
import SiteFooter from "./SiteFooter";
import { getConsent } from "@/lib/consent";

const AboutSection = () => {
  const { ref, isVisible } = useScrollReveal(0.15);
  const [showCookieSettings, setShowCookieSettings] = useState(false);

  return (
    <div className="w-full h-full overflow-y-auto">
      <section className="py-16 px-6 md:px-10">
        <div className="max-w-3xl mx-auto">
          <p className="text-caption uppercase tracking-widest text-dim mb-12 font-sans">
            About F-Labs
          </p>

          <div
            ref={ref as React.RefObject<HTMLDivElement>}
            className={`space-y-8 transition-all duration-1000 ease-out ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <p className="text-body text-subtle font-sans leading-loose">
              Forgiveness Labs is an applied research studio dedicated to reimagining how societies remember, identify, and respond to crisis. Rather than simply developing new technologies, we focus on crafting ethical infrastructure that honours the complexities of memory and identity while responding intelligently to moments of crisis. Our approach begins with a deep inquiry into the movement of memory, tracing its migration across borders, the transmission between generations, and its transformation within evolving social and technological systems.
            </p>

            <p className="text-body text-subtle font-sans leading-loose">
              At the heart of Forgiveness Labs is a commitment to designing tools and systems that uphold human dignity, respect individual and collective consent, and reinforce cultural authority. We reject models that extract value from communities without their participation or understanding. Instead, we work to ensure that technology becomes a protective force, one that safeguards stories, traditions as well as identities while remaining accountable to those it serves.
            </p>

            <p className="text-body text-subtle font-sans leading-loose">
              Our practice bridges rigorous research with hands-on experimentation, moving fluidly between theoretical exploration and the development of early-stage systems. We pay particular attention to the needs of under-documented and displaced communities, always refusing surveillance and coercive data practices. Our aim is to support the self-determination and resilience of these groups by providing infrastructure that is as thoughtful and adaptable as the people who use it.
            </p>

            <p className="text-body text-subtle font-sans leading-loose">
              Forgiveness Labs operates with a long-term perspective, embracing a deliberate pace that allows for reflection, learning, and selectivity. Our philosophy is grounded in the belief that robust infrastructure should not attempt to control or override human nature. Instead, it should exist in harmony with the nuanced realities of human life, fostering trust, agency, and continuity for generations to come.
            </p>
          </div>

          {/* Visage Labs Callout */}
          <div className="mt-24 glass p-6 md:p-8">
            <span className="text-micro text-dim uppercase tracking-[0.2em] block mb-4">
              Tier 3 · Separate Entity
            </span>
            <h3 className="text-title font-serif mb-3">Visage Labs, Inc. (2026)</h3>
            <p className="text-body text-subtle mb-4">
              Digital identity compliance and human likeness licensing.
            </p>
            <a
              href="https://visagelabs.net"
              target="_blank"
              rel="noopener noreferrer"
              className="text-caption text-dim hover:text-foreground transition-colors duration-300 link-underline uppercase tracking-[0.15em]"
            >
              visagelabs.net →
            </a>
          </div>

          {/* Investor CTA */}
          <div className="mt-24">
            <p className="text-body text-subtle leading-relaxed mb-6">
              Are you an investor exploring venture studios or a deeply technical engineer interested in long-horizon work? We'd like to hear from you.
            </p>
            <Link
              to="/contact"
              className="text-caption text-dim hover:text-foreground transition-colors duration-300 link-underline"
            >
              Contact Us
            </Link>
          </div>

        </div>
      </section>

      <CookieSettings open={showCookieSettings} onOpenChange={setShowCookieSettings} currentStatus={getConsent()} />
      <SiteFooter />
    </div>
  );
};

export default AboutSection;
